import { useState } from 'react'
import type { Setting, SettingSource } from '../../api/types'
import { Badge, Button, Card, ErrorNote, LoadingBlock, errorMessage, type Tone } from '../../components/ui'
import { useClearSetting, useConnectStreamWebhook, useSaveSetting, useSettings, useTestProvider } from '../../hooks/useAdmin'
import { groupBy, timeAgo } from '../../utils/format'

const SOURCES: Record<SettingSource, { tone: Tone; label: string }> = {
  database: { tone: 'accent', label: 'Saved here' },
  environment: { tone: 'neutral', label: 'From environment' },
  default: { tone: 'neutral', label: 'Default' },
  unset: { tone: 'warn', label: 'Not set' },
}

export function AdminProvidersPage() {
  const settings = useSettings()
  if (settings.isPending) return <LoadingBlock />
  if (settings.isError) return <ErrorNote error={settings.error} />
  return (
    <div className="stack-lg">
      <p className="muted">
        Keys saved here are encrypted before they are stored and are never shown in full again. A saved value overrides the
        environment variable.
      </p>
      {groupBy(settings.data, (s) => s.group).map(([group, items]) => {
        const provider = items[0]?.provider
        return (
          <Card
            key={group}
            title={group}
            actions={
              provider ? (
                <>
                  <TestKey provider={provider} />
                  {provider === 'cloudflare' && <ConnectWebhook />}
                </>
              ) : undefined
            }
          >
            <div className="settings">
              {items.map((setting) => (
                <SettingRow key={setting.key} setting={setting} />
              ))}
            </div>
          </Card>
        )
      })}
    </div>
  )
}

function TestKey({ provider }: { provider: string }) {
  const test = useTestProvider()
  return (
    <>
      {test.data && <Badge tone={test.data.ok ? 'ok' : 'danger'}>{test.data.message}</Badge>}
      {test.error && <Badge tone="danger">{errorMessage(test.error)}</Badge>}
      <Button variant="secondary" busy={test.isPending} onClick={() => test.mutate(provider)}>Test key</Button>
    </>
  )
}

/** Point Stream's recording webhook at this deployment; the secret it returns is saved. */
function ConnectWebhook() {
  const connect = useConnectStreamWebhook()
  return (
    <>
      {connect.data && <Badge tone="ok">Webhook connected</Badge>}
      {connect.error && <Badge tone="danger">{errorMessage(connect.error)}</Badge>}
      <Button variant="secondary" busy={connect.isPending} onClick={() => connect.mutate()}>Connect webhook</Button>
    </>
  )
}

function SettingRow({ setting }: { setting: Setting }) {
  const [value, setValue] = useState('')
  const save = useSaveSetting()
  const clear = useClearSetting()
  const source = SOURCES[setting.source]
  return (
    <div className="setting">
      <div className="setting-head">
        <div>
          <div className="setting-label">{setting.label}</div>
          <div className="muted small">{setting.help}</div>
        </div>
        <Badge tone={source.tone}>{source.label}</Badge>
      </div>
      <div className="setting-current small">
        {setting.display ? <code>{setting.display}</code> : <span className="muted">No value</span>}
        {setting.env && <span className="muted"> · env fallback {setting.env}</span>}
        {setting.updated_at && setting.source === 'database' && <span className="muted"> · saved {timeAgo(setting.updated_at)}</span>}
      </div>
      {setting.error && <ErrorNote message={setting.error} />}
      <form
        className="setting-form"
        onSubmit={(event) => {
          event.preventDefault()
          save.mutate({ key: setting.key, value }, { onSuccess: () => setValue('') })
        }}
      >
        <input
          type={setting.secret ? 'password' : 'text'}
          autoComplete="off"
          spellCheck={false}
          aria-label={setting.label}
          placeholder={setting.secret ? 'Paste a new key' : (setting.default ?? '')}
          value={value}
          onChange={(e) => setValue(e.target.value)}
        />
        <Button type="submit" busy={save.isPending} disabled={!value.trim()}>Save</Button>
        {setting.source === 'database' && (
          <Button variant="ghost" busy={clear.isPending} onClick={() => clear.mutate(setting.key)}>Clear</Button>
        )}
      </form>
      {save.error && <ErrorNote error={save.error} />}
      {clear.error && <ErrorNote error={clear.error} />}
    </div>
  )
}
